import { ArrowRight, Zap } from 'lucide-react';
import { DocBreadcrumbs } from '../components/DocBreadcrumbs';
import { DocPagination } from '../components/DocPagination';
import { PagePurpose } from '../components/PagePurpose';
import { EXPLORER_URL } from '../config';

const palette = {
  accent: '#0F6B7C',
  border: '#D4E2F4',
  tint: '#F4F7FC',
};

type Provenance = 'open' | 'licensed' | 'borrowed' | 'partner' | 'sb-elaborated';

const provenanceStyles: Record<Provenance, { label: string; bg: string; color: string }> = {
  open: { label: 'Open', bg: '#E7F6ED', color: '#0F7A43' },
  licensed: { label: 'Licensed', bg: '#FEF3C7', color: '#B45309' },
  borrowed: { label: 'Borrowed', bg: '#F3E8FF', color: '#7E22CE' },
  partner: { label: 'Partner IP', bg: '#E0F2FE', color: '#0369A1' },
  'sb-elaborated': { label: 'SB-elaborated', bg: '#E0F2F1', color: '#0F6B7C' },
};

interface SourceItem {
  name: string;
  note: string;
  /** True when this data is queryable live in the API explorer. */
  liveInExplorer?: boolean;
  link?: { label: string; href: string };
}

interface ProvenanceGroup {
  id: Provenance;
  title: string;
  blurb: string;
  items: SourceItem[];
}

const provenanceGroups: ProvenanceGroup[] = [
  {
    id: 'open',
    title: 'Open',
    blurb: 'Free and reusable — ours to share.',
    items: [
      {
        name: 'CBS — boundaries, VK500 population grid, kerncijfers',
        note: 'CC-BY / CC0. Boundaries and the VK500 grid are served as OGC layers.',
        liveInExplorer: true,
      },
      { name: 'PDOK — BAG, BGT, TOP10NL', note: 'Open national base registers (addresses, buildings, topography).' },
      { name: 'Klimaateffectatlas', note: 'CC-BY 4.0. Climate-adaptation reference layers.' },
      { name: 'OpenStreetMap (OSM)', note: 'Community basemap for streets, POIs, and land use.' },
      { name: 'NWB', note: 'Nationaal Wegenbestand — the open Dutch road register.' },
      { name: 'GTFS', note: 'Open public-transport timetable feeds.' },
      { name: 'NDW open feeds', note: 'CC0. Open road-traffic feeds from the national portal.' },
    ],
  },
  {
    id: 'licensed',
    title: 'Licensed',
    blurb: 'We hold a licence to use it.',
    items: [
      {
        name: 'NRM / LMS traffic model',
        note: 'Under RWS terms; commercial use allowed. Scenario road networks served via the NRM viewer.',
        liveInExplorer: true,
      },
      {
        name: 'TravelTime',
        note: 'Annual licence — isochrones and routing. Trial exposure via the TravelTime processes.',
        liveInExplorer: true,
      },
    ],
  },
  {
    id: 'borrowed',
    title: 'Borrowed',
    blurb: 'Access via a client or partner contract — NOT ours to redistribute.',
    items: [
      {
        name: 'TomTom O/D',
        note: "Via gemeente Amsterdam's contract; expires 2028. Usable inside that engagement only.",
      },
      {
        name: 'NDW tier-3 (mijnNDW)',
        note: 'Partner access — richer than the open feeds, bound by the partner agreement.',
      },
    ],
  },
  {
    id: 'partner',
    title: 'Partner IP',
    blurb: 'Built by a partner; SB does the data-prep and visualisation.',
    items: [
      {
        name: 'Move Mobility accessibility engines',
        note: 'BOP / bollenkaart reachability. SB prepares and visualises; the engine is Move Mobility IP.',
        liveInExplorer: true,
      },
    ],
  },
  {
    id: 'sb-elaborated',
    title: 'SB-elaborated',
    blurb: 'Our methodology IP built on top of the sources above.',
    items: [
      {
        name: 'ODiN v3 canonical',
        note: 'Our cleaned, canonical build of the ODiN travel survey. Queryable via the ODIN methods.',
        liveInExplorer: true,
        link: { label: 'See the methods', href: '/documentation/methodologies' },
      },
      {
        name: 'VK500 urbanisation index',
        note: "Roland's urbanisation indicator on the VK500 grid.",
        liveInExplorer: true,
        link: { label: 'See the methods', href: '/documentation/methodologies' },
      },
      {
        name: 'GLO donut',
        note: 'Spatial-health quick-scan (donutmodel).',
        link: { label: 'See the methods', href: '/documentation/methodologies' },
      },
    ],
  },
];

function ProvenanceBadge({ type }: { type: Provenance }) {
  const style = provenanceStyles[type];
  return (
    <span
      className="rounded-full px-3 py-1 text-xs font-semibold"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {style.label}
    </span>
  );
}

function LiveInExplorer() {
  return (
    <a
      href={EXPLORER_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: '#E7F6ED', color: '#0F7A43' }}
    >
      <Zap size={12} aria-hidden />
      Queryable live
    </a>
  );
}

export function ExternalSourcesPage() {
  return (
    <div className="space-y-8">
      <DocBreadcrumbs currentTitle="Data Sources" />

      <PagePurpose
        title="Where our data comes from"
        audience="Project managers, data analysts, and anyone preparing client proposals"
        description="Our sources grouped by provenance — what's ours to share, what we license, and what we can only use inside a specific engagement. Use this to know the provenance of any data behind a deliverable."
        badge="Reference"
      />

      <section
        className="rounded-3xl border bg-white p-6 shadow-sm"
        style={{ borderColor: palette.border, backgroundColor: palette.tint }}
      >
        <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: palette.accent }}>
          Read this first
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-sb-black">Data sources, by provenance</h1>
        <p className="mt-3 max-w-3xl text-base text-sb-dark-grey">
          Some of this is ours to share, some we license, and some we can only use inside a specific
          engagement. This page is explicit about which. The{' '}
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold align-middle"
            style={{ backgroundColor: '#E7F6ED', color: '#0F7A43' }}
          >
            <Zap size={11} aria-hidden /> Queryable live
          </span>{' '}
          marker means you can hit that data in the API explorer right now.
        </p>
      </section>

      {provenanceGroups.map((group) => (
        <section
          key={group.id}
          className="rounded-3xl border bg-white p-8 shadow-sm"
          style={{ borderColor: palette.border }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <ProvenanceBadge type={group.id} />
            <h2 className="text-2xl font-semibold text-sb-black">{group.title}</h2>
          </div>
          <p className="mt-2 text-sm text-sb-dark-grey">{group.blurb}</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {group.items.map((item) => (
              <div
                key={item.name}
                className="rounded-2xl border p-5"
                style={{ borderColor: palette.border, backgroundColor: palette.tint }}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h3 className="text-base font-semibold text-sb-black">{item.name}</h3>
                  {item.liveInExplorer && <LiveInExplorer />}
                </div>
                <p className="mt-2 text-sm text-sb-dark-grey">{item.note}</p>
                {item.link && (
                  <a
                    href={item.link.href}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-semibold"
                    style={{ color: palette.accent }}
                  >
                    {item.link.label} <ArrowRight size={15} aria-hidden />
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <DocPagination currentPageId="external-sources" />
    </div>
  );
}
